import toast from 'react-hot-toast';

class ToastManager {
  constructor() {
    this.activeToasts = new Set();
    this.maxToasts = 2;
  }

  // Add a new toast and manage the limit
  addToast(toastFunction, message, options = {}) {
    const isSticky = options?.duration === Infinity;
    // If we have reached the limit, remove the oldest toast
    if (!isSticky && this.activeToasts.size >= this.maxToasts) {
      const oldestToast = Array.from(this.activeToasts)[0];
      toast.dismiss(oldestToast);
      this.activeToasts.delete(oldestToast);
    }

    // Default options for better UX
    const defaultOptions = {
      duration: 2000, // 2 seconds default
      dismissible: true,
      ...options
    };

    // Create new toast
    const toastId = toastFunction(message, {
      ...defaultOptions,
      onClose: (id) => {
        this.activeToasts.delete(id);
        if (options.onClose) {
          options.onClose(id);
        }
      }
    });

    // Track the new toast
    this.activeToasts.add(toastId);
    return toastId;
  }

  success(message, options = {}) {
    return this.addToast(toast.success, message, {
      duration: 2000, // 2 seconds for success
      ...options
    });
  }

  error(message, options = {}) {
    return this.addToast(toast.error, message, {
      duration: 2000, // 2 seconds for errors
      ...options
    });
  }

  info(message, options = {}) {
    return this.addToast(toast, message, {
      icon: 'ℹ️',
      duration: 2000,
      ...options
    });
  }

  loading(message, options = {}) {
    return this.addToast(toast.loading, message, {
      duration: 2000, // Safe 2-second timeout, dismissible on touch
      ...options
    });
  }

  custom(message, options = {}) {
    return this.addToast(toast, message, options);
  }

  dismiss(toastId) {
    if (toastId) {
      this.activeToasts.delete(toastId);
      return toast.dismiss(toastId);
    } else {
      // Dismiss all
      this.activeToasts.clear();
      return toast.dismiss();
    }
  }

  remove(toastId) {
    this.activeToasts.delete(toastId);
    return toast.remove(toastId);
  }
}

// Create singleton instance
const toastManager = new ToastManager();

export default toastManager;